import { useState } from "react";
import { useAuth } from "../../../contexts/Auth";
import { useMutation, useQueryClient } from "react-query";
import { useNavigate } from "react-router-dom";
import { updateUserHandle } from "../../../services/userApi";
import { trackEvent } from "../../../shared/analytics";
import {
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Input,
  InputGroup,
  InputLeftAddon,
  Spacer,
  Text,
  Divider,
  useToast,
} from "@chakra-ui/react";
import {
  getUserHandleError,
  USER_HANDLE_MAX_LENGTH,
  USER_HANDLE_MIN_LENGTH,
} from "../../../core/userHandle";

const UserHandleForm = () => {
  const { authData } = useAuth();
  const [username, setUsername] = useState(authData.userHandle || "");
  const [error, setError] = useState("");
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const toast = useToast();

  const mutation = useMutation(
    ({ userHandle }) => {
      return updateUserHandle(userHandle);
    },
    {
      onSuccess: ({ response }) => {
        if (!response) {
          trackEvent("user_handle_updated");
          toast({
            title: "User handle updated",
            description: `Thank you! Your user handle has been updated.`,
            status: "success",
            duration: 5000,
            isClosable: true,
          });
          queryClient.invalidateQueries("user");
          navigate("/profile");
        } else {
          toast({
            title: response.data.message,
            description: response.data?.errors.user_handle[0],
            status: "error",
            duration: 5000,
            isClosable: true,
          });
        }
      },
    }
  );

  const handleUsernameChange = (nextValue) => {
    setUsername(nextValue);
    setError(getUserHandleError(nextValue, { required: true }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const userHandleError = getUserHandleError(username, { required: true });
    setError(userHandleError);

    if (userHandleError) {
      toast({
        title: "Invalid user handle",
        description: userHandleError,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }
    mutation.mutate({ userHandle: username });
  };

  // const Caption = () => (
  //   <View style={styles.captionContainer}>
  //     <Text style={styles.captionText}>
  //       {error ? error : "Can only contains letters, numbers, underscores, hyphens and periods."}
  //     </Text>
  //   </View>
  // );

  return (
    <Box p="3">
      <form onSubmit={handleSubmit}>
        <FormControl isInvalid={error !== ""}>
          <FormLabel>User handle</FormLabel>
          <InputGroup>
            <InputLeftAddon>@</InputLeftAddon>
            <Input
              value={username}
              label="User handle"
              placeholder="Enter your handle"
              autoCapitalize="none"
              autoComplete="username"
              maxLength={USER_HANDLE_MAX_LENGTH}
              minLength={USER_HANDLE_MIN_LENGTH}
              pattern="[a-zA-Z0-9_.-]+"
              required
              onChange={(e) => handleUsernameChange(e.target.value)}
            />
          </InputGroup>
          {error !== "" ? (
            <FormErrorMessage>{error}</FormErrorMessage>
          ) : (
            <FormHelperText>
              Use letters, numbers, underscores, hyphens, or periods. Spaces are
              not allowed.
            </FormHelperText>
          )}
        </FormControl>
        <Spacer my="3" />
        <Button
          isDisabled={
            mutation.isLoading ||
            username === authData.userHandle ||
            getUserHandleError(username, { required: true }) !== ""
          }
          type="submit"
          colorScheme="red"
          color="white"
          fontSize="md"
        >
          {mutation.isLoading ? "Updating handle..." : "Update my handle"}
        </Button>

        <Divider mt="6" />
        <Text textColor="gray.500" pt="3">
          Your user handle will be displayed on your profile and to other users
          in the community.
        </Text>
      </form>
    </Box>
  );
};

export default UserHandleForm;
